import { useCallback, useMemo } from "react";
import { useHistoricalFitnessProfiles } from "../../FitnessProfile/HistoricalProfiles/useHistoricalFitnessProfiles";
import {
  FitnessProfileAlgorithm,
  TrainingProgressionUpdateType,
} from "@WahooFitness/cloud-client-types";

function useTrainingProgressEstimate() {
  const { historicalProfilesData } = useHistoricalFitnessProfiles();
  const hasAlreadyTakenQuiz = useMemo(
    () =>
      historicalProfilesData.some(
        (profile) => profile.fitness_profile_algorithm_id === FitnessProfileAlgorithm.FourDPQuiz
      ),
    [historicalProfilesData]
  );
  const question6Map = useMemo(
    () => ({
      "<1": 1,
      "1-3": 2,
      "3-6": 4.5,
      "6-10": 8,
      ">10": 12,
    }),
    []
  );

  const question7LoadMap = useMemo(
    () => ({
      "0%": 30,
      "0-25%": 40,
      "25-50%": 48,
      "50-75%": 55,
      "65-100%": 65,
    }),
    []
  );

  const question7DistributionMap = useMemo(
    () => ({
      "0%": { dim_1_low: 1, dim_1_mod: 0, dim_1_high: 0, dim_2_pwr: 0 },
      "0-25%": { dim_1_low: 0.7, dim_1_mod: 0.2, dim_1_high: 0.1, dim_2_pwr: 0 },
      "25-50%": { dim_1_low: 0.6, dim_1_mod: 0.2, dim_1_high: 0.1, dim_2_pwr: 0.1 },
      "50-75%": { dim_1_low: 0.5, dim_1_mod: 0.1, dim_1_high: 0.2, dim_2_pwr: 0.2 },
      "65-100%": { dim_1_low: 0.4, dim_1_mod: 0.1, dim_1_high: 0.25, dim_2_pwr: 0.25 },
    }),
    []
  );

  const createTrainingProgressEstimateFromResponses = useCallback(
    (
      responses: Record<string, Array<string | Date | number | null>>
    ): TrainingProgressionUpdateType | undefined => {
      if (hasAlreadyTakenQuiz) {
        return;
      }
      const trainingLoad =
        (question6Map[responses["6"]?.[0] as keyof typeof question6Map] ||
          2 * question7LoadMap[responses["7"]?.[0] as keyof typeof question7LoadMap] ||
          40) / 7;
      const trainingLoadDistribution =
        question7DistributionMap[responses["7"]?.[0] as keyof typeof question7DistributionMap] ||
        question7DistributionMap["0%"];
      return {
        dim1_pwr_low_stl: trainingLoad * trainingLoadDistribution.dim_1_low,
        dim1_pwr_mod_stl: trainingLoad * trainingLoadDistribution.dim_1_mod,
        dim1_pwr_high_stl: trainingLoad * trainingLoadDistribution.dim_1_high,
        dim2_pwr_stl: trainingLoad * trainingLoadDistribution.dim_2_pwr,
        dim2_cad_stl: 0.0,
        dim3_pwr_stl: 0.0,
        dim3_anr_stl: 0.0,
        dim4_pwr_stl: 0.0,
        dim4_cad_stl: 0.0,
        dim4_trq_stl: 0.0,
        dim1_pwr_low_ltl: trainingLoad * trainingLoadDistribution.dim_1_low,
        dim1_pwr_mod_ltl: trainingLoad * trainingLoadDistribution.dim_1_mod,
        dim1_pwr_high_ltl: trainingLoad * trainingLoadDistribution.dim_1_high,
        dim2_pwr_ltl: trainingLoad * trainingLoadDistribution.dim_2_pwr,
        dim2_cad_ltl: 0.0,
        dim3_pwr_ltl: 0.0,
        dim3_anr_ltl: 0.0,
        dim4_pwr_ltl: 0.0,
        dim4_cad_ltl: 0.0,
        dim4_trq_ltl: 0.0,
      };
    },
    [hasAlreadyTakenQuiz, question6Map, question7DistributionMap, question7LoadMap]
  );

  return { createTrainingProgressEstimateFromResponses };
}

export default useTrainingProgressEstimate;
